import { useState, useEffect, useCallback, useRef } from 'react';
import { useApiError } from '@infrastructure/api/hooks/useApiError';
import { normalizeAllForList } from '@core/utils/listQuery';
import ApiResponse from '@core/types/apiResponse';

/**
 * The normalized list envelope a service must resolve to: a page of rows plus
 * its pagination metadata. Church/District/Nation list responses already match
 * `{ data, meta }`.
 */
export interface ListServiceResult<T, M> {
  data: T[];
  meta: M | null;
}

/**
 * The contract a feature provides to {@link usePaginatedList}. Assemble one with
 * {@link createListService} so the entity-specific bits (endpoint, validation,
 * row mapping) live in one config object.
 */
export interface ListService<T, P, M> {
  validate: (params: P) => { isValid: boolean; processedParams: P; errors: string[] };
  fetch: (params: P) => Promise<ApiResponse<ListServiceResult<T, M>>>;
  process: (items: T[]) => T[];
}

export interface UsePaginatedListOptions<P> {
  /** Keys that, when present in an `updateParams` call, reset `page` to 1. */
  filterResetFields?: (keyof P)[];
  /** Keys passed to `normalizeAllForList` when merging params (drops `all` when a filter is active). */
  normalizeKeys?: (keyof P & string)[];
  /** Human label used in fallback error messages, e.g. "churches". */
  entityLabel?: string;
}

export interface UsePaginatedListResult<T, P, M> {
  items: T[];
  loading: boolean;
  error: string | null;
  pagination: M | null;
  refetch: () => void;
  updateParams: (newParams: Partial<P>) => void;
  validationErrors: string[];
}

/**
 * Shared paginated-list state machine: validation → fetch → process, with
 * loading/error/pagination/validation state and param management. Replaces the
 * byte-for-byte-identical per-entity list hooks (church/district/nation).
 *
 * Pass a stable, module-level `service` (see {@link createListService}) so the
 * fetch effect doesn't re-run every render.
 */
export function usePaginatedList<
  T,
  P extends { page?: number; perPage?: number; all?: boolean },
  M
>(
  service: ListService<T, P, M>,
  initialParams: P = {} as P,
  options: UsePaginatedListOptions<P> = {}
): UsePaginatedListResult<T, P, M> {
  const { filterResetFields = [], normalizeKeys = [], entityLabel = 'records' } = options;

  const [items, setItems] = useState<T[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState<M | null>(null);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [params, setParams] = useState<P>(
    initialParams.all ? { ...initialParams } : ({ perPage: 10, page: 1, ...initialParams } as P)
  );

  const showError = useApiError();
  const paramsRef = useRef(params);
  paramsRef.current = params;

  // Re-sync when the caller's initialParams change (e.g. a parent selection).
  // Skip the first render since those values are already applied in useState above.
  const serialized = JSON.stringify(initialParams);
  const firstSyncRef = useRef(true);
  useEffect(() => {
    if (firstSyncRef.current) {
      firstSyncRef.current = false;
      return;
    }
    setParams((prev) => ({ ...prev, ...initialParams, page: 1 }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serialized]);

  const fetchData = useCallback(async () => {
    const currentParams = paramsRef.current;
    setLoading(true);
    setError(null);
    setValidationErrors([]);

    try {
      const validation = service.validate(currentParams);
      if (!validation.isValid) {
        setValidationErrors(validation.errors);
        setItems([]);
        setPagination(null);
        setLoading(false);
        return;
      }

      const response = await service.fetch(validation.processedParams);

      if (response.isSuccess()) {
        const result = response.getData()!;
        setItems(service.process(result.data));
        setPagination(result.meta);
      } else {
        const errorData = response.getError();
        setError(errorData?.detail || `Failed to fetch ${entityLabel}`);
        if (errorData) {
          showError(errorData);
        }
        setItems([]);
        setPagination(null);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : `Failed to fetch ${entityLabel}`);
      setItems([]);
      setPagination(null);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    void fetchData();
  }, [params, fetchData]);

  const refetch = useCallback(() => {
    void fetchData();
  }, [fetchData]);

  const updateParams = useCallback((newParams: Partial<P>) => {
    setParams((prev) => {
      const filterChanged = filterResetFields.some((key) => key in newParams);
      const merged = {
        ...prev,
        ...newParams,
        page: filterChanged ? 1 : ((newParams.page as number | undefined) ?? prev.page),
      } as P;
      return normalizeAllForList(merged as Record<string, any>, normalizeKeys as string[]) as P;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { items, loading, error, pagination, refetch, updateParams, validationErrors };
}
